import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GraduationCap, ChevronLeft, ChevronRight, Check } from "lucide-react";

// First-login guided tour. Mounted once inside the authenticated Layout. Auto-opens only when the
// backend says should_show (never-started); otherwise stays closed. Replayable via the
// 'cc-replay-tutorial' window event (fired from the profile menu). Fully skippable.
export default function TutorialOverlay() {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);

  const beginFromState = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/tutorial");
      setSteps(data.steps || []);
      if (data.should_show && (data.steps || []).length) {
        setIdx(0); setOpen(true);
        api.post("/onboarding/tutorial/start").catch(() => {});
      }
    } catch { /* tour is non-critical */ }
  }, []);

  useEffect(() => { beginFromState(); }, [beginFromState]);

  useEffect(() => {
    const replay = async () => {
      try {
        const { data } = await api.post("/onboarding/tutorial/replay");
        setSteps(data.steps || []);
        setIdx(0); setOpen(true);
      } catch { /* ignore */ }
    };
    window.addEventListener("cc-replay-tutorial", replay);
    return () => window.removeEventListener("cc-replay-tutorial", replay);
  }, []);

  const total = steps.length;
  const step = steps[idx];
  const last = idx >= total - 1;

  const goNext = async () => {
    if (last) {
      setOpen(false);
      api.post("/onboarding/tutorial/complete").catch(() => {});
      return;
    }
    const n = idx + 1;
    setIdx(n);
    api.post("/onboarding/tutorial/progress", { last_step: n }).catch(() => {});
  };
  const goBack = () => setIdx((i) => Math.max(0, i - 1));
  const skip = () => { setOpen(false); api.post("/onboarding/tutorial/skip").catch(() => {}); };

  if (!open || !step) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); }}>
      <DialogContent data-testid="tutorial-overlay" className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 text-brand mb-1"><GraduationCap size={18} /><span className="text-xs font-bold uppercase tracking-wide">CaterCTRL tour · {idx + 1}/{total}</span></div>
          <DialogTitle data-testid="tutorial-step-title">{step.title}</DialogTitle>
          <DialogDescription data-testid="tutorial-step-body" className="text-sm text-slate-600 pt-1">{step.body}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 py-2" aria-hidden>
          {steps.map((s, i) => <span key={s.key} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-brand" : "w-2 bg-slate-200"}`} />)}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={skip} data-testid="tutorial-skip" className="text-sm text-slate-400 hover:text-slate-600 underline">Skip tour</button>
          <div className="flex gap-2">
            {idx > 0 && <Button variant="outline" onClick={goBack} data-testid="tutorial-back"><ChevronLeft size={16} className="mr-1" /> Back</Button>}
            <Button onClick={goNext} data-testid="tutorial-next" className="bg-brand hover:bg-brand-hover">
              {last ? <><Check size={16} className="mr-1" /> Finish</> : <>Next <ChevronRight size={16} className="ml-1" /></>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
